// Plugins
import i18n from '@/plugins/i18n';

// Constants
import { DEFAULT_LOCALE } from '@/constants/locale-constants';

/** Nome do país no idioma da tela; em pt-BR fica o nome de sempre da lista. */
const regionName = (code, ptBrName) => {
  if (i18n.locale === DEFAULT_LOCALE) return ptBrName;

  try {
    return new Intl.DisplayNames([i18n.locale], { type: 'region' }).of(code) || ptBrName;
  } catch (error) {
    return ptBrName;
  }
};

/** Texto lido na hora (getter): a lista troca de idioma sem mudar quem a usa. */
const sepaCountryOption = (value, ptBrName) => ({
  get text() {
    return regionName(value, ptBrName);
  },
  value
});

/** Descrição do código de propósito SWIFT, traduzida pelo próprio código (value). */
const swiftPaymentCodeOption = (value) => ({
  get text() {
    return i18n.t(`formCustomer.swiftPaymentCodesUi.${value}`);
  },
  value
});

export const PAYMENT_METHOD_OPTIONS = [
  { value: 'wire', get label() { return i18n.t('formCustomer.internationalBank.bankTypeWire'); }, countryCode: null, soon: false },
  {
    value: 'international_swift',
    get label() { return i18n.t('formCustomer.internationalBank.bankTypeSwift'); },
    countryCode: null,
    soon: false
  },
  { value: 'ach', label: 'ACH', countryCode: 'US', soon: false },
  { value: 'rtp', get label() { return i18n.t('formCustomer.internationalBank.bankTypeRtp'); }, countryCode: 'US', soon: false },
  { value: 'transfers_3', label: 'Transfers 3.0', countryCode: 'AR', soon: false },
  { value: 'ach_colombia', get label() { return i18n.t('formCustomer.internationalBank.bankTypeAchColombia'); }, countryCode: 'CO', soon: false },
  { value: 'spei', label: 'SPEI', countryCode: 'MX', soon: false },
  { value: 'sepa', label: 'SEPA EUR', countryCode: 'EU', soon: false },
  { value: 'faster_payments', get label() { return i18n.t('formCustomer.internationalBank.bankTypeFasterPayments'); }, countryCode: 'GB', soon: false }
];

export const ACCOUNT_TYPE_OPTIONS = [
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.accountTypes.checking'); }, value: 'checking' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.accountTypes.savings'); }, value: 'savings' }
];

export const ACCOUNT_CLASS_OPTIONS = [
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.accountClasses.individual'); }, value: 'individual' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.accountClasses.business'); }, value: 'business' }
];

export const RECIPIENT_RELATIONSHIP_OPTIONS = [
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.firstParty'); }, value: 'first_party' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.employee'); }, value: 'employee' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.independentContractor'); }, value: 'independent_contractor' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.vendorOrSupplier'); }, value: 'vendor_or_supplier' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.subsidiaryOrAffiliate'); }, value: 'subsidiary_or_affiliate' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.merchantOrPartner'); }, value: 'merchant_or_partner' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.customer'); }, value: 'customer' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.landlord'); }, value: 'landlord' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.family'); }, value: 'family' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.recipientRelationships.other'); }, value: 'other' }
];

export const COLOMBIA_DOCUMENT_TYPE_OPTIONS = [
  { text: 'Cédula de Ciudadanía', value: 'CC' },
  { text: 'Cédula de Extranjería', value: 'CE' },
  { text: 'NIT', value: 'NIT' },
  { text: 'Pasaporte', value: 'PASS' },
  { text: 'PEP (Permiso Especial de Permanencia)', value: 'PEP' }
];

export const COLOMBIA_BANK_CODE_OPTIONS = [
  { text: 'Banco de Bogotá', value: '0001' },
  { text: 'Banco Popular', value: '0002' },
  { text: 'Banco de Occidente', value: '0023' },
  { text: 'Banco AV Villas', value: '0052' },
  { text: 'Banco Agrario', value: '0040' },
  { text: 'Banco Caja Social', value: '0051' },
  { text: 'Bancolombia', value: '0007' },
  { text: 'Banco de Colombia', value: '0009' },
  { text: 'Banco GNB Sudameris', value: '0012' },
  { text: 'Banco Falabella', value: '0014' },
  { text: 'Banco Santander', value: '0013' },
  { text: 'Banco Davivienda', value: '0051' },
  { text: 'Banco BBVA', value: '0013' },
  { text: 'Banco Cooperativo Coopcentral', value: '0056' }
];

export const TRANSFERS_3_TYPE_OPTIONS = [
  { text: 'CVU', value: 'cvu' },
  { text: 'CBU', value: 'cbu' }
];

/**
 * Regras de validação de código postal por país (ISO 3166-1 alpha-2).
 * regex: formato exigido
 * example: exemplo para exibir ao usuário
 */
export const POSTAL_CODE_RULES = {
  BR: { regex: /^\d{5}-?\d{3}$/, example: '01310-100' },
  US: { regex: /^\d{5}(-\d{4})?$/, get example() { return i18n.t('formCustomer.internationalBankConstantsUi.usPostalCodeExample'); } },
  GB: { regex: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, example: 'SW1A 1AA' },
  CA: { regex: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i, example: 'K1A 0B1' },
  DE: { regex: /^\d{5}$/, example: '10115' },
  FR: { regex: /^\d{5}$/, example: '75008' },
  ES: { regex: /^\d{5}$/, example: '28001' },
  IT: { regex: /^\d{5}$/, example: '00100' },
  PT: { regex: /^\d{4}-?\d{3}$/, example: '1000-001' },
  NL: { regex: /^\d{4}\s?[A-Z]{2}$/i, example: '1012 AB' },
  BE: { regex: /^\d{4}$/, example: '1000' },
  AT: { regex: /^\d{4}$/, example: '1010' },
  CH: { regex: /^\d{4}$/, example: '8001' },
  SE: { regex: /^\d{3}\s?\d{2}$/, example: '111 22' },
  NO: { regex: /^\d{4}$/, example: '0150' },
  DK: { regex: /^\d{4}$/, example: '1050' },
  FI: { regex: /^\d{5}$/, example: '00100' },
  PL: { regex: /^\d{2}-?\d{3}$/, example: '00-001' },
  IE: { regex: /^[A-Z\d]{3}\s?[A-Z\d]{4}$/i, example: 'D02 AF30' },
  JP: { regex: /^\d{3}-?\d{4}$/, example: '100-0001' },
  AU: { regex: /^\d{4}$/, example: '2000' },
  MX: { regex: /^\d{5}$/, example: '01000' },
  AR: { regex: /^[A-Z]?\d{4}[A-Z]{0,3}$/i, example: 'C1425' },
  CO: { regex: /^\d{6}$/, example: '110111' },
  IN: { regex: /^\d{6}$/, example: '110001' },
  CL: { regex: /^\d{7}$/, example: '8320000' },
};

/**
 * Países SEPA onde pagamentos para contas empresariais (business) não são suportados.
 * Nesses países, apenas account_class: individual é aceito pela BlindPay.
 * Fonte: https://blindpay.com/docs/kb/payment-methods
 */
export const SEPA_BUSINESS_BLOCKED_COUNTRIES = new Set(['AT', 'EE', 'FI', 'FR', 'LT', 'NO', 'PT']);

export const SEPA_COUNTRY_OPTIONS = [
  sepaCountryOption('AL', 'Albânia'),
  sepaCountryOption('AD', 'Andorra'),
  sepaCountryOption('AT', 'Áustria'),
  sepaCountryOption('BE', 'Bélgica'),
  sepaCountryOption('BG', 'Bulgária'),
  sepaCountryOption('CY', 'Chipre'),
  sepaCountryOption('HR', 'Croácia'),
  sepaCountryOption('DK', 'Dinamarca'),
  sepaCountryOption('SK', 'Eslováquia'),
  sepaCountryOption('SI', 'Eslovênia'),
  sepaCountryOption('ES', 'Espanha'),
  sepaCountryOption('EE', 'Estônia'),
  sepaCountryOption('FI', 'Finlândia'),
  sepaCountryOption('FR', 'França'),
  sepaCountryOption('DE', 'Alemanha'),
  sepaCountryOption('GR', 'Grécia'),
  sepaCountryOption('HU', 'Hungria'),
  sepaCountryOption('IE', 'Irlanda'),
  sepaCountryOption('IS', 'Islândia'),
  sepaCountryOption('IT', 'Itália'),
  sepaCountryOption('LV', 'Letônia'),
  sepaCountryOption('LI', 'Liechtenstein'),
  sepaCountryOption('LT', 'Lituânia'),
  sepaCountryOption('LU', 'Luxemburgo'),
  sepaCountryOption('MK', 'Macedônia do Norte'),
  sepaCountryOption('MT', 'Malta'),
  sepaCountryOption('MD', 'Moldávia'),
  sepaCountryOption('MC', 'Mônaco'),
  sepaCountryOption('ME', 'Montenegro'),
  sepaCountryOption('NO', 'Noruega'),
  sepaCountryOption('NL', 'Países Baixos'),
  sepaCountryOption('PL', 'Polônia'),
  sepaCountryOption('PT', 'Portugal'),
  sepaCountryOption('GB', 'Reino Unido'),
  sepaCountryOption('CZ', 'República Tcheca'),
  sepaCountryOption('RO', 'Romênia'),
  sepaCountryOption('SM', 'San Marino'),
  sepaCountryOption('VA', 'Santa Sé (Vaticano)'),
  sepaCountryOption('SE', 'Suécia'),
  sepaCountryOption('CH', 'Suíça')
];

export const SPEI_PROTOCOL_OPTIONS = [
  { text: 'CLABE', value: 'clabe' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.speiProtocols.debitCard'); }, value: 'debitcard' },
  { get text() { return i18n.t('formCustomer.internationalBankConstantsUi.speiProtocols.phoneNumber'); }, value: 'phonenum' }
];

// Fonte: BlindPay GET /v1/available-bank-details?rail=spei_bitso.
// Códigos no formato oficial Bitso (37XXX desenvolvimento, 40XXX comerciais, 90XXX fintechs/cajas).
// Resincronizar quando a BlindPay publicar novos participantes.
export const SPEI_INSTITUTION_CODE_OPTIONS = [
  { text: 'Actinver', value: '40133' },
  { text: 'Afirme', value: '40062' },
  { text: 'Albo', value: '90721' },
  { text: 'Altor', value: '90660' },
  { text: 'Arcus FI', value: '90706' },
  { text: 'ASP Integra OPC', value: '90659' },
  { text: 'Babien', value: '37166' },
  { text: 'Banamex', value: '40002' },
  { text: 'Banco Azteca', value: '40127' },
  { text: 'Banco Covalto', value: '40154' },
  { text: 'Banco del Bajío', value: '40030' },
  { text: 'Banco Inbursa', value: '40036' },
  { text: 'Banco S3', value: '40160' },
  { text: 'Bancomext', value: '37006' },
  { text: 'Bancoppel', value: '40137' },
  { text: 'Bancrea', value: '40152' },
  { text: 'Banjercito', value: '37019' },
  { text: 'Bank of America', value: '40106' },
  { text: 'Bank of China', value: '40159' },
  { text: 'Bankaool', value: '40147' },
  { text: 'Banobras', value: '37009' },
  { text: 'Banorte', value: '40072' },
  { text: 'Banregio', value: '40058' },
  { text: 'Bansi', value: '40060' },
  { text: 'Barclays', value: '40129' },
  { text: 'BBASE', value: '40145' },
  { text: 'BBVA Bancomer', value: '40012' },
  { text: 'BMonex', value: '40112' },
  { text: 'Caja Pop Mexica', value: '90677' },
  { text: 'Caja Telefonist', value: '90683' },
  { text: 'Cashi Cuenta', value: '90715' },
  { text: 'Citi Mexico', value: '40124' },
  { text: 'Clip', value: '90730' },
  { text: 'Compartamos Banco', value: '40130' },
  { text: 'Consubanco', value: '40140' },
  { text: 'Coopdesarrollo', value: '90725' },
  { text: 'Credicapital', value: '90652' },
  { text: 'Crediclub', value: '90688' },
  { text: 'Cristobal Colon', value: '90680' },
  { text: 'Cuenca', value: '90723' },
  { text: 'Dep y Pag Dig', value: '90729' },
  { text: 'Donde', value: '40151' },
  { text: 'Finamex', value: '90616' },
  { text: 'Finco Pay', value: '90734' },
  { text: 'Fincomun', value: '90634' },
  { text: 'Fintoc', value: '90738' },
  { text: 'Fondeadora', value: '90699' },
  { text: 'Fondo (FIRA)', value: '90685' },
  { text: 'GBM', value: '90601' },
  { text: 'Hey Banco', value: '40167' },
  { text: 'Hipotecaria Fed', value: '37168' },
  { text: 'HSBC', value: '40021' },
  { text: 'ICBC', value: '40155' },
  { text: 'Indeval', value: '90902' },
  { text: 'Inmobiliario', value: '40150' },
  { text: 'Intercam Banco', value: '40136' },
  { text: 'Invex', value: '40059' },
  { text: 'JP Morgan', value: '40110' },
  { text: 'Kapital', value: '40128' },
  { text: 'Klar', value: '90661' },
  { text: 'Kuspit', value: '90653' },
  { text: 'Libertad', value: '90670' },
  { text: 'Masari', value: '90602' },
  { text: 'Mercado Pago W', value: '90722' },
  { text: 'MexPago', value: '90720' },
  { text: 'Mifel', value: '40042' },
  { text: 'Mizuho Bank', value: '40158' },
  { text: 'MonexCB', value: '90600' },
  { text: 'MUFG', value: '40108' },
  { text: 'Multiva Banco', value: '40132' },
  { text: 'NAFIN', value: '37135' },
  { text: 'Nubank', value: '40638' },
  { text: 'NVIO Pagos Mexico', value: '90710' },
  { text: 'Pagatodo', value: '40148' },
  { text: 'Peibo', value: '90732' },
  { text: 'PPBalanceMX', value: '90714' },
  { text: 'Profuturo', value: '90620' },
  { text: 'Revolut Bank', value: '40170' },
  { text: 'Sabadell', value: '40156' },
  { text: 'Santander', value: '40014' },
  { text: 'Scotiabank', value: '40044' },
  { text: 'Shinhan', value: '40157' },
  { text: 'Spin by OXXO', value: '90728' },
  { text: 'STP', value: '90646' },
  { text: 'Tesored', value: '90703' },
  { text: 'Transfer', value: '90684' },
  { text: 'Transfer Directo', value: '90727' },
  { text: 'TRF', value: '90631' },
  { text: 'UALA', value: '40138' },
  { text: 'Unagra', value: '90656' },
  { text: 'Valmex', value: '90617' },
  { text: 'Value', value: '90605' },
  { text: 'Ve por Mas', value: '40113' },
  { text: 'Volkswagen', value: '40141' }
];

/**
 * Códigos de propósito SWIFT por país, conforme BlindPay API.
 * Cada item: { text (traduzido em formCustomer.swiftPaymentCodesUi), value: 'xx_swift_codigo' }
 * Prefixos: ae_, bh_, ch_, cn_, hk_, id_, in_, jp_, ke_, ph_, pk_, za_
 */
export const SWIFT_PAYMENT_CODE_OPTIONS = [
  // AE — Emirados Árabes
  swiftPaymentCodeOption('ae_swift_str'),
  swiftPaymentCodeOption('ae_swift_tcs'),
  swiftPaymentCodeOption('ae_swift_ipc'),
  swiftPaymentCodeOption('ae_swift_ifs'),
  swiftPaymentCodeOption('ae_swift_sts'),
  swiftPaymentCodeOption('ae_swift_pms'),
  swiftPaymentCodeOption('ae_swift_its'),
  swiftPaymentCodeOption('ae_swift_gde'),
  swiftPaymentCodeOption('ae_swift_ots'),
  swiftPaymentCodeOption('ae_swift_tts'),
  // BH — Bahrein
  swiftPaymentCodeOption('bh_swift_afa'),
  swiftPaymentCodeOption('bh_swift_afl'),
  swiftPaymentCodeOption('bh_swift_ats'),
  swiftPaymentCodeOption('bh_swift_cea'),
  swiftPaymentCodeOption('bh_swift_cel'),
  swiftPaymentCodeOption('bh_swift_chc'),
  swiftPaymentCodeOption('bh_swift_dla'),
  swiftPaymentCodeOption('bh_swift_dlf'),
  swiftPaymentCodeOption('bh_swift_dll'),
  swiftPaymentCodeOption('bh_swift_doe'),
  swiftPaymentCodeOption('bh_swift_dsa'),
  swiftPaymentCodeOption('bh_swift_dsf'),
  swiftPaymentCodeOption('bh_swift_dsl'),
  swiftPaymentCodeOption('bh_swift_fam'),
  swiftPaymentCodeOption('bh_swift_fda'),
  swiftPaymentCodeOption('bh_swift_fdl'),
  swiftPaymentCodeOption('bh_swift_fia'),
  swiftPaymentCodeOption('bh_swift_fil'),
  swiftPaymentCodeOption('bh_swift_fis'),
  swiftPaymentCodeOption('bh_swift_fsa'),
  swiftPaymentCodeOption('bh_swift_fsl'),
  swiftPaymentCodeOption('bh_swift_gde'),
  swiftPaymentCodeOption('bh_swift_gdi'),
  swiftPaymentCodeOption('bh_swift_gms'),
  swiftPaymentCodeOption('bh_swift_gos'),
  swiftPaymentCodeOption('bh_swift_gri'),
  swiftPaymentCodeOption('bh_swift_ifs'),
  swiftPaymentCodeOption('bh_swift_igd'),
  swiftPaymentCodeOption('bh_swift_iid'),
  swiftPaymentCodeOption('bh_swift_ins'),
  swiftPaymentCodeOption('bh_swift_iod'),
  swiftPaymentCodeOption('bh_swift_iol'),
  swiftPaymentCodeOption('bh_swift_ipc'),
  swiftPaymentCodeOption('bh_swift_ish'),
  swiftPaymentCodeOption('bh_swift_isl'),
  swiftPaymentCodeOption('bh_swift_iss'),
  swiftPaymentCodeOption('bh_swift_its'),
  swiftPaymentCodeOption('bh_swift_ldl'),
  swiftPaymentCodeOption('bh_swift_lds'),
  swiftPaymentCodeOption('bh_swift_lea'),
  swiftPaymentCodeOption('bh_swift_lel'),
  swiftPaymentCodeOption('bh_swift_lla'),
  swiftPaymentCodeOption('bh_swift_lll'),
  swiftPaymentCodeOption('bh_swift_ots'),
  swiftPaymentCodeOption('bh_swift_pip'),
  swiftPaymentCodeOption('bh_swift_pms'),
  swiftPaymentCodeOption('bh_swift_ppa'),
  swiftPaymentCodeOption('bh_swift_ppl'),
  swiftPaymentCodeOption('bh_swift_prr'),
  swiftPaymentCodeOption('bh_swift_prs'),
  swiftPaymentCodeOption('bh_swift_rda'),
  swiftPaymentCodeOption('bh_swift_rdl'),
  swiftPaymentCodeOption('bh_swift_rds'),
  swiftPaymentCodeOption('bh_swift_rea'),
  swiftPaymentCodeOption('bh_swift_rel'),
  swiftPaymentCodeOption('bh_swift_rfs'),
  swiftPaymentCodeOption('bh_swift_rls'),
  swiftPaymentCodeOption('bh_swift_sal'),
  swiftPaymentCodeOption('bh_swift_sco'),
  swiftPaymentCodeOption('bh_swift_sla'),
  swiftPaymentCodeOption('bh_swift_sll'),
  swiftPaymentCodeOption('bh_swift_str'),
  swiftPaymentCodeOption('bh_swift_sts'),
  swiftPaymentCodeOption('bh_swift_tcp'),
  swiftPaymentCodeOption('bh_swift_tcr'),
  swiftPaymentCodeOption('bh_swift_tcs'),
  swiftPaymentCodeOption('bh_swift_tts'),
  // CH — Suíça
  swiftPaymentCodeOption('ch_swift_ccdndr'),
  swiftPaymentCodeOption('ch_swift_cctfdr'),
  swiftPaymentCodeOption('ch_swift_cgoddr'),
  swiftPaymentCodeOption('ch_swift_cocadr'),
  swiftPaymentCodeOption('ch_swift_cstrdr'),
  swiftPaymentCodeOption('ch_swift_remtdr'),
  // CN — China
  swiftPaymentCodeOption('cn_swift_ccdndr'),
  swiftPaymentCodeOption('cn_swift_cctfdr'),
  swiftPaymentCodeOption('cn_swift_cgoddr'),
  swiftPaymentCodeOption('cn_swift_cocadr'),
  swiftPaymentCodeOption('cn_swift_cstrdr'),
  swiftPaymentCodeOption('cn_swift_remtdr'),
  // HK — Hong Kong
  swiftPaymentCodeOption('hk_swift_charitabledonation'),
  swiftPaymentCodeOption('hk_swift_goods'),
  swiftPaymentCodeOption('hk_swift_personal'),
  swiftPaymentCodeOption('hk_swift_services'),
  // JP — Jordânia (prefixo jp_ na BlindPay)
  swiftPaymentCodeOption('jp_swift_101'),
  swiftPaymentCodeOption('jp_swift_102'),
  swiftPaymentCodeOption('jp_swift_103'),
  swiftPaymentCodeOption('jp_swift_104'),
  swiftPaymentCodeOption('jp_swift_105'),
  swiftPaymentCodeOption('jp_swift_106'),
  swiftPaymentCodeOption('jp_swift_107'),
  swiftPaymentCodeOption('jp_swift_108'),
  swiftPaymentCodeOption('jp_swift_109'),
  swiftPaymentCodeOption('jp_swift_110'),
  swiftPaymentCodeOption('jp_swift_111'),
  swiftPaymentCodeOption('jp_swift_1111'),
  swiftPaymentCodeOption('jp_swift_1112'),
  swiftPaymentCodeOption('jp_swift_201'),
  swiftPaymentCodeOption('jp_swift_202'),
  swiftPaymentCodeOption('jp_swift_203'),
  swiftPaymentCodeOption('jp_swift_301'),
  swiftPaymentCodeOption('jp_swift_302'),
  swiftPaymentCodeOption('jp_swift_303'),
  swiftPaymentCodeOption('jp_swift_305'),
  swiftPaymentCodeOption('jp_swift_306'),
  swiftPaymentCodeOption('jp_swift_401'),
  swiftPaymentCodeOption('jp_swift_402'),
  swiftPaymentCodeOption('jp_swift_403'),
  swiftPaymentCodeOption('jp_swift_404'),
  swiftPaymentCodeOption('jp_swift_601'),
  swiftPaymentCodeOption('jp_swift_602'),
  swiftPaymentCodeOption('jp_swift_603'),
  swiftPaymentCodeOption('jp_swift_604'),
  swiftPaymentCodeOption('jp_swift_801'),
  swiftPaymentCodeOption('jp_swift_802'),
  swiftPaymentCodeOption('jp_swift_803'),
  swiftPaymentCodeOption('jp_swift_804'),
  swiftPaymentCodeOption('jp_swift_805'),
  swiftPaymentCodeOption('jp_swift_806'),
  swiftPaymentCodeOption('jp_swift_807'),
  swiftPaymentCodeOption('jp_swift_809'),
  swiftPaymentCodeOption('jp_swift_810'),
  swiftPaymentCodeOption('jp_swift_811'),
  swiftPaymentCodeOption('jp_swift_812'),
  swiftPaymentCodeOption('jp_swift_813'),
  swiftPaymentCodeOption('jp_swift_815'),
  swiftPaymentCodeOption('jp_swift_902'),
  swiftPaymentCodeOption('jp_swift_903'),
  swiftPaymentCodeOption('jp_swift_904'),
  swiftPaymentCodeOption('jp_swift_1001'),
  swiftPaymentCodeOption('jp_swift_1002'),
  swiftPaymentCodeOption('jp_swift_1003'),
  swiftPaymentCodeOption('jp_swift_1004'),
  swiftPaymentCodeOption('jp_swift_1101'),
  swiftPaymentCodeOption('jp_swift_1102'),
  swiftPaymentCodeOption('jp_swift_1103'),
  swiftPaymentCodeOption('jp_swift_1104'),
  swiftPaymentCodeOption('jp_swift_1105'),
  swiftPaymentCodeOption('jp_swift_1106'),
  swiftPaymentCodeOption('jp_swift_1107'),
  swiftPaymentCodeOption('jp_swift_1108'),
  swiftPaymentCodeOption('jp_swift_1109'),
  swiftPaymentCodeOption('jp_swift_1110'),
  swiftPaymentCodeOption('jp_swift_1201'),
  swiftPaymentCodeOption('jp_swift_1202'),
  swiftPaymentCodeOption('jp_swift_204'),
  swiftPaymentCodeOption('jp_swift_205'),
  swiftPaymentCodeOption('jp_swift_206'),
  swiftPaymentCodeOption('jp_swift_207'),
  swiftPaymentCodeOption('jp_swift_208'),
  swiftPaymentCodeOption('jp_swift_209'),
  swiftPaymentCodeOption('jp_swift_304'),
  swiftPaymentCodeOption('jp_swift_307'),
  swiftPaymentCodeOption('jp_swift_501'),
  swiftPaymentCodeOption('jp_swift_502'),
  swiftPaymentCodeOption('jp_swift_503'),
  swiftPaymentCodeOption('jp_swift_504'),
  swiftPaymentCodeOption('jp_swift_701'),
  swiftPaymentCodeOption('jp_swift_702'),
  swiftPaymentCodeOption('jp_swift_703'),
  swiftPaymentCodeOption('jp_swift_704'),
  swiftPaymentCodeOption('jp_swift_705'),
  swiftPaymentCodeOption('jp_swift_808'),
  swiftPaymentCodeOption('jp_swift_814'),
  swiftPaymentCodeOption('jp_swift_816'),
  swiftPaymentCodeOption('jp_swift_817'),
  swiftPaymentCodeOption('jp_swift_818'),
  swiftPaymentCodeOption('jp_swift_901'),
  // KE — Quênia
  swiftPaymentCodeOption('ke_swift_airb'),
  swiftPaymentCodeOption('ke_swift_chc'),
  swiftPaymentCodeOption('ke_swift_comu'),
  swiftPaymentCodeOption('ke_swift_cons'),
  swiftPaymentCodeOption('ke_swift_cort'),
  swiftPaymentCodeOption('ke_swift_divd'),
  swiftPaymentCodeOption('ke_swift_educ'),
  swiftPaymentCodeOption('ke_swift_foex'),
  swiftPaymentCodeOption('ke_swift_govt'),
  swiftPaymentCodeOption('ke_swift_hlfd'),
  swiftPaymentCodeOption('ke_swift_hlti'),
  swiftPaymentCodeOption('ke_swift_loan'),
  swiftPaymentCodeOption('ke_swift_mdcs'),
  swiftPaymentCodeOption('ke_swift_merc'),
  swiftPaymentCodeOption('ke_swift_paye'),
  swiftPaymentCodeOption('ke_swift_psco'),
  swiftPaymentCodeOption('ke_swift_refu'),
  swiftPaymentCodeOption('ke_swift_rent'),
  swiftPaymentCodeOption('ke_swift_sala'),
  swiftPaymentCodeOption('ke_swift_savg'),
  swiftPaymentCodeOption('ke_swift_scho'),
  swiftPaymentCodeOption('ke_swift_ship'),
  swiftPaymentCodeOption('ke_swift_taxs'),
  swiftPaymentCodeOption('ke_swift_ubil'),
  swiftPaymentCodeOption('ke_swift_vatx'),
  swiftPaymentCodeOption('ke_swift_whld'),
  swiftPaymentCodeOption('ke_swift_1001'),
  swiftPaymentCodeOption('ke_swift_1002'),
  swiftPaymentCodeOption('ke_swift_1101'),
  swiftPaymentCodeOption('ke_swift_1102'),
  swiftPaymentCodeOption('ke_swift_1201'),
  swiftPaymentCodeOption('ke_swift_1202'),
  swiftPaymentCodeOption('ke_swift_1206'),
  swiftPaymentCodeOption('ke_swift_1501'),
  swiftPaymentCodeOption('ke_swift_1518'),
  swiftPaymentCodeOption('ke_swift_1519'),
  swiftPaymentCodeOption('ke_swift_1527'),
  swiftPaymentCodeOption('ke_swift_1801'),
  swiftPaymentCodeOption('ke_swift_1802'),
  swiftPaymentCodeOption('ke_swift_1908'),
  swiftPaymentCodeOption('ke_swift_2101'),
  swiftPaymentCodeOption('ke_swift_2301'),
  swiftPaymentCodeOption('ke_swift_2501'),
  swiftPaymentCodeOption('ke_swift_2901'),
  swiftPaymentCodeOption('ke_swift_3001'),
  swiftPaymentCodeOption('ke_swift_3100'),
  swiftPaymentCodeOption('ke_swift_3101'),
  swiftPaymentCodeOption('ke_swift_3103'),
  swiftPaymentCodeOption('ke_swift_3200'),
  swiftPaymentCodeOption('ke_swift_3304'),
  swiftPaymentCodeOption('ke_swift_3509'),
  swiftPaymentCodeOption('ke_swift_3514'),
  swiftPaymentCodeOption('ke_swift_3801'),
  swiftPaymentCodeOption('ke_swift_4103'),
  swiftPaymentCodeOption('ke_swift_4301'),
  swiftPaymentCodeOption('ke_swift_4601'),
  swiftPaymentCodeOption('ke_swift_4702'),
  swiftPaymentCodeOption('ke_swift_512'),
  swiftPaymentCodeOption('ke_swift_6001'),
  swiftPaymentCodeOption('ke_swift_6002'),
  swiftPaymentCodeOption('ke_swift_6101'),
  swiftPaymentCodeOption('ke_swift_6102'),
  swiftPaymentCodeOption('ke_swift_6301'),
  swiftPaymentCodeOption('ke_swift_6401'),
  swiftPaymentCodeOption('ke_swift_6402'),
  swiftPaymentCodeOption('ke_swift_6501'),
  swiftPaymentCodeOption('ke_swift_6601'),
  swiftPaymentCodeOption('ke_swift_adtx'),
  swiftPaymentCodeOption('ke_swift_artx'),
  swiftPaymentCodeOption('ke_swift_bech'),
  swiftPaymentCodeOption('ke_swift_bsd'),
  swiftPaymentCodeOption('ke_swift_bttx'),
  swiftPaymentCodeOption('ke_swift_ccmc'),
  swiftPaymentCodeOption('ke_swift_cere'),
  swiftPaymentCodeOption('ke_swift_cfr'),
  swiftPaymentCodeOption('ke_swift_cgtx'),
  swiftPaymentCodeOption('ke_swift_clot'),
  swiftPaymentCodeOption('ke_swift_cotx'),
  swiftPaymentCodeOption('ke_swift_csdk'),
  swiftPaymentCodeOption('ke_swift_edtx'),
  swiftPaymentCodeOption('ke_swift_farm'),
  swiftPaymentCodeOption('ke_swift_fuel'),
  swiftPaymentCodeOption('ke_swift_gokx'),
  swiftPaymentCodeOption('ke_swift_holi'),
  swiftPaymentCodeOption('ke_swift_ibld'),
  swiftPaymentCodeOption('ke_swift_inpc'),
  swiftPaymentCodeOption('ke_swift_insu'),
  swiftPaymentCodeOption('ke_swift_inte'),
  swiftPaymentCodeOption('ke_swift_intx'),
  swiftPaymentCodeOption('ke_swift_invs'),
  swiftPaymentCodeOption('ke_swift_istx'),
  swiftPaymentCodeOption('ke_swift_licf'),
  swiftPaymentCodeOption('ke_swift_lifi'),
  swiftPaymentCodeOption('ke_swift_mach'),
  swiftPaymentCodeOption('ke_swift_mafc'),
  swiftPaymentCodeOption('ke_swift_pena'),
  swiftPaymentCodeOption('ke_swift_pl39'),
  swiftPaymentCodeOption('ke_swift_pl40'),
  swiftPaymentCodeOption('ke_swift_pl41'),
  swiftPaymentCodeOption('ke_swift_pl42'),
  swiftPaymentCodeOption('ke_swift_pl43'),
  swiftPaymentCodeOption('ke_swift_pl44'),
  swiftPaymentCodeOption('ke_swift_pl45'),
  swiftPaymentCodeOption('ke_swift_pl46'),
  swiftPaymentCodeOption('ke_swift_pl47'),
  swiftPaymentCodeOption('ke_swift_pl48'),
  swiftPaymentCodeOption('ke_swift_pl49'),
  swiftPaymentCodeOption('ke_swift_pl50'),
  swiftPaymentCodeOption('ke_swift_pl51'),
  swiftPaymentCodeOption('ke_swift_pl52'),
  swiftPaymentCodeOption('ke_swift_pl53'),
  swiftPaymentCodeOption('ke_swift_ppti'),
  swiftPaymentCodeOption('ke_swift_prpy'),
  swiftPaymentCodeOption('ke_swift_relg'),
  swiftPaymentCodeOption('ke_swift_ritx'),
  swiftPaymentCodeOption('ke_swift_rlwy'),
  swiftPaymentCodeOption('ke_swift_sdtx'),
  swiftPaymentCodeOption('ke_swift_swlf'),
  swiftPaymentCodeOption('ke_swift_taxr'),
  swiftPaymentCodeOption('ke_swift_tbil'),
  swiftPaymentCodeOption('ke_swift_tith'),
  swiftPaymentCodeOption('ke_swift_totx'),
  swiftPaymentCodeOption('ke_swift_trac'),
  swiftPaymentCodeOption('ke_swift_vipn'),
  // ZA — África do Sul
  swiftPaymentCodeOption('za_swift_105'),
  swiftPaymentCodeOption('za_swift_220'),
  swiftPaymentCodeOption('za_swift_231'),
  swiftPaymentCodeOption('za_swift_242'),
  swiftPaymentCodeOption('za_swift_250'),
  swiftPaymentCodeOption('za_swift_251'),
  swiftPaymentCodeOption('za_swift_265'),
  swiftPaymentCodeOption('za_swift_266'),
  swiftPaymentCodeOption('za_swift_275'),
  swiftPaymentCodeOption('za_swift_276'),
  swiftPaymentCodeOption('za_swift_280'),
  swiftPaymentCodeOption('za_swift_285'),
  swiftPaymentCodeOption('za_swift_287'),
  swiftPaymentCodeOption('za_swift_288'),
  swiftPaymentCodeOption('za_swift_289'),
  swiftPaymentCodeOption('za_swift_290'),
  swiftPaymentCodeOption('za_swift_291'),
  swiftPaymentCodeOption('za_swift_296'),
  swiftPaymentCodeOption('za_swift_297'),
  swiftPaymentCodeOption('za_swift_308'),
  swiftPaymentCodeOption('za_swift_401'),
  swiftPaymentCodeOption('za_swift_10101'),
  swiftPaymentCodeOption('za_swift_10301'),
  swiftPaymentCodeOption('za_swift_27201'),
  // IN — Índia
  swiftPaymentCodeOption('in_swift_p0001'),
  swiftPaymentCodeOption('in_swift_p0002'),
  swiftPaymentCodeOption('in_swift_p0003'),
  swiftPaymentCodeOption('in_swift_p0004'),
  swiftPaymentCodeOption('in_swift_p0005'),
  swiftPaymentCodeOption('in_swift_p0006'),
  swiftPaymentCodeOption('in_swift_p0007'),
  swiftPaymentCodeOption('in_swift_p0008'),
  swiftPaymentCodeOption('in_swift_p0009'),
  swiftPaymentCodeOption('in_swift_p0010'),
  swiftPaymentCodeOption('in_swift_p0011'),
  swiftPaymentCodeOption('in_swift_p0012'),
  swiftPaymentCodeOption('in_swift_p0013'),
  swiftPaymentCodeOption('in_swift_p0014'),
  swiftPaymentCodeOption('in_swift_p0015'),
  swiftPaymentCodeOption('in_swift_p0016'),
  swiftPaymentCodeOption('in_swift_p0017'),
  swiftPaymentCodeOption('in_swift_p0019'),
  swiftPaymentCodeOption('in_swift_p0020'),
  swiftPaymentCodeOption('in_swift_p0021'),
  swiftPaymentCodeOption('in_swift_p0022'),
  swiftPaymentCodeOption('in_swift_p0024'),
  swiftPaymentCodeOption('in_swift_p0025'),
  swiftPaymentCodeOption('in_swift_p0028'),
  swiftPaymentCodeOption('in_swift_p0029'),
  swiftPaymentCodeOption('in_swift_p0099'),
  swiftPaymentCodeOption('in_swift_p0101'),
  swiftPaymentCodeOption('in_swift_p0102'),
  swiftPaymentCodeOption('in_swift_p0103'),
  swiftPaymentCodeOption('in_swift_p0104'),
  swiftPaymentCodeOption('in_swift_p0105'),
  swiftPaymentCodeOption('in_swift_p0107'),
  swiftPaymentCodeOption('in_swift_p0108'),
  swiftPaymentCodeOption('in_swift_p0109'),
  // ID — Indonésia
  swiftPaymentCodeOption('id_swift_2011'),
  swiftPaymentCodeOption('id_swift_2012'),
  swiftPaymentCodeOption('id_swift_2015'),
  swiftPaymentCodeOption('id_swift_2018'),
  swiftPaymentCodeOption('id_swift_2019'),
  swiftPaymentCodeOption('id_swift_2097'),
  swiftPaymentCodeOption('id_swift_2098'),
  swiftPaymentCodeOption('id_swift_2127'),
  swiftPaymentCodeOption('id_swift_2129'),
  swiftPaymentCodeOption('id_swift_2150'),
  swiftPaymentCodeOption('id_swift_2163'),
  swiftPaymentCodeOption('id_swift_2193'),
  swiftPaymentCodeOption('id_swift_2194'),
  swiftPaymentCodeOption('id_swift_2197'),
  swiftPaymentCodeOption('id_swift_2198'),
  swiftPaymentCodeOption('id_swift_2203'),
  swiftPaymentCodeOption('id_swift_2204'),
  swiftPaymentCodeOption('id_swift_2206'),
  swiftPaymentCodeOption('id_swift_2207'),
  swiftPaymentCodeOption('id_swift_2221'),
  swiftPaymentCodeOption('id_swift_2222'),
  swiftPaymentCodeOption('id_swift_2231'),
  swiftPaymentCodeOption('id_swift_2232'),
  swiftPaymentCodeOption('id_swift_2233'),
  swiftPaymentCodeOption('id_swift_2240'),
  swiftPaymentCodeOption('id_swift_2241'),
  swiftPaymentCodeOption('id_swift_2242'),
  swiftPaymentCodeOption('id_swift_2243'),
  swiftPaymentCodeOption('id_swift_2244'),
  swiftPaymentCodeOption('id_swift_2245'),
  swiftPaymentCodeOption('id_swift_2246'),
  swiftPaymentCodeOption('id_swift_2247'),
  swiftPaymentCodeOption('id_swift_2251'),
  swiftPaymentCodeOption('id_swift_2252'),
  swiftPaymentCodeOption('id_swift_2255'),
  swiftPaymentCodeOption('id_swift_2256'),
  swiftPaymentCodeOption('id_swift_2257'),
  swiftPaymentCodeOption('id_swift_2261'),
  swiftPaymentCodeOption('id_swift_2262'),
  swiftPaymentCodeOption('id_swift_2263'),
  swiftPaymentCodeOption('id_swift_2264'),
  swiftPaymentCodeOption('id_swift_2271'),
  swiftPaymentCodeOption('id_swift_2272'),
  swiftPaymentCodeOption('id_swift_2273'),
  swiftPaymentCodeOption('id_swift_2274'),
  swiftPaymentCodeOption('id_swift_2275'),
  swiftPaymentCodeOption('id_swift_2276'),
  swiftPaymentCodeOption('id_swift_2277'),
  swiftPaymentCodeOption('id_swift_2278'),
  swiftPaymentCodeOption('id_swift_2279'),
  swiftPaymentCodeOption('id_swift_2280'),
  swiftPaymentCodeOption('id_swift_2282'),
  swiftPaymentCodeOption('id_swift_2299'),
  swiftPaymentCodeOption('id_swift_2311'),
  swiftPaymentCodeOption('id_swift_2321'),
  swiftPaymentCodeOption('id_swift_2322'),
  swiftPaymentCodeOption('id_swift_2323'),
  swiftPaymentCodeOption('id_swift_2331'),
  swiftPaymentCodeOption('id_swift_2332'),
  swiftPaymentCodeOption('id_swift_2333'),
  swiftPaymentCodeOption('id_swift_2341'),
  swiftPaymentCodeOption('id_swift_2342'),
  swiftPaymentCodeOption('id_swift_2351'),
  swiftPaymentCodeOption('id_swift_2352'),
  swiftPaymentCodeOption('id_swift_2353'),
  swiftPaymentCodeOption('id_swift_2354'),
  swiftPaymentCodeOption('id_swift_2361'),
  swiftPaymentCodeOption('id_swift_2362'),
  swiftPaymentCodeOption('id_swift_2363'),
  swiftPaymentCodeOption('id_swift_2364'),
  swiftPaymentCodeOption('id_swift_2365'),
  swiftPaymentCodeOption('id_swift_2366'),
  swiftPaymentCodeOption('id_swift_2371'),
  swiftPaymentCodeOption('id_swift_2372'),
  swiftPaymentCodeOption('id_swift_2375'),
  swiftPaymentCodeOption('id_swift_2376'),
  swiftPaymentCodeOption('id_swift_2377'),
  swiftPaymentCodeOption('id_swift_2378'),
  swiftPaymentCodeOption('id_swift_2379'),
  swiftPaymentCodeOption('id_swift_2380'),
  swiftPaymentCodeOption('id_swift_2381'),
  swiftPaymentCodeOption('id_swift_2382'),
  swiftPaymentCodeOption('id_swift_2383'),
  swiftPaymentCodeOption('id_swift_2384'),
  swiftPaymentCodeOption('id_swift_2385'),
  swiftPaymentCodeOption('id_swift_2386'),
  swiftPaymentCodeOption('id_swift_2387'),
  swiftPaymentCodeOption('id_swift_2388'),
  swiftPaymentCodeOption('id_swift_2389'),
  swiftPaymentCodeOption('id_swift_2390'),
  swiftPaymentCodeOption('id_swift_2391'),
  swiftPaymentCodeOption('id_swift_2392'),
  swiftPaymentCodeOption('id_swift_2393'),
  swiftPaymentCodeOption('id_swift_2394'),
  swiftPaymentCodeOption('id_swift_2395'),
  swiftPaymentCodeOption('id_swift_2396'),
  swiftPaymentCodeOption('id_swift_2397'),
  swiftPaymentCodeOption('id_swift_2398'),
  swiftPaymentCodeOption('id_swift_2400'),
  swiftPaymentCodeOption('id_swift_2405'),
  swiftPaymentCodeOption('id_swift_2411'),
  swiftPaymentCodeOption('id_swift_2412'),
  swiftPaymentCodeOption('id_swift_2413'),
  swiftPaymentCodeOption('id_swift_2421'),
  swiftPaymentCodeOption('id_swift_2422'),
  swiftPaymentCodeOption('id_swift_2423'),
  swiftPaymentCodeOption('id_swift_2431'),
  swiftPaymentCodeOption('id_swift_2432'),
  swiftPaymentCodeOption('id_swift_2433'),
  swiftPaymentCodeOption('id_swift_2441'),
  swiftPaymentCodeOption('id_swift_2442'),
  swiftPaymentCodeOption('id_swift_2443'),
  swiftPaymentCodeOption('id_swift_2450'),
  swiftPaymentCodeOption('id_swift_2461'),
  swiftPaymentCodeOption('id_swift_2462'),
  swiftPaymentCodeOption('id_swift_2466'),
  swiftPaymentCodeOption('id_swift_2467'),
  swiftPaymentCodeOption('id_swift_2468'),
  swiftPaymentCodeOption('id_swift_2469'),
  swiftPaymentCodeOption('id_swift_2480'),
  swiftPaymentCodeOption('id_swift_2490'),
  swiftPaymentCodeOption('id_swift_2495'),
  swiftPaymentCodeOption('id_swift_2501'),
  swiftPaymentCodeOption('id_swift_2502'),
  swiftPaymentCodeOption('id_swift_2511'),
  swiftPaymentCodeOption('id_swift_2512'),
  swiftPaymentCodeOption('id_swift_2521'),
  swiftPaymentCodeOption('id_swift_2522'),
  swiftPaymentCodeOption('id_swift_2523'),
  swiftPaymentCodeOption('id_swift_2524'),
  swiftPaymentCodeOption('id_swift_2525'),
  swiftPaymentCodeOption('id_swift_2526'),
  swiftPaymentCodeOption('id_swift_2531'),
  swiftPaymentCodeOption('id_swift_2532'),
  swiftPaymentCodeOption('id_swift_2533'),
  swiftPaymentCodeOption('id_swift_2541'),
  swiftPaymentCodeOption('id_swift_2546'),
  swiftPaymentCodeOption('id_swift_2547'),
  swiftPaymentCodeOption('id_swift_2550'),
  swiftPaymentCodeOption('id_swift_2560'),
  swiftPaymentCodeOption('id_swift_2570'),
  swiftPaymentCodeOption('id_swift_2580'),
  swiftPaymentCodeOption('id_swift_2590'),
  swiftPaymentCodeOption('id_swift_2600'),
  swiftPaymentCodeOption('id_swift_2610'),
  swiftPaymentCodeOption('id_swift_2615'),
  swiftPaymentCodeOption('id_swift_2616'),
  swiftPaymentCodeOption('id_swift_2630'),
  swiftPaymentCodeOption('id_swift_2640'),
  swiftPaymentCodeOption('id_swift_2651'),
  swiftPaymentCodeOption('id_swift_2652'),
  swiftPaymentCodeOption('id_swift_2660'),
  swiftPaymentCodeOption('id_swift_2670'),
  swiftPaymentCodeOption('id_swift_2701'),
  swiftPaymentCodeOption('id_swift_2702'),
  swiftPaymentCodeOption('id_swift_2705'),
  swiftPaymentCodeOption('id_swift_2710'),
  swiftPaymentCodeOption('id_swift_2716'),
  swiftPaymentCodeOption('id_swift_2717'),
  swiftPaymentCodeOption('id_swift_2720'),
  swiftPaymentCodeOption('id_swift_2725'),
  swiftPaymentCodeOption('id_swift_2730'),
  swiftPaymentCodeOption('id_swift_2731'),
  swiftPaymentCodeOption('id_swift_2741'),
  swiftPaymentCodeOption('id_swift_2742'),
  swiftPaymentCodeOption('id_swift_2743'),
  swiftPaymentCodeOption('id_swift_2751'),
  swiftPaymentCodeOption('id_swift_2752'),
  swiftPaymentCodeOption('id_swift_2760'),
  swiftPaymentCodeOption('id_swift_2765'),
  swiftPaymentCodeOption('id_swift_2766'),
  swiftPaymentCodeOption('id_swift_2767'),
  swiftPaymentCodeOption('id_swift_2770'),
  swiftPaymentCodeOption('id_swift_2802'),
  swiftPaymentCodeOption('id_swift_2803'),
  swiftPaymentCodeOption('id_swift_2804'),
  swiftPaymentCodeOption('id_swift_2808'),
  swiftPaymentCodeOption('id_swift_2809'),
  swiftPaymentCodeOption('id_swift_2811'),
  swiftPaymentCodeOption('id_swift_2812'),
  swiftPaymentCodeOption('id_swift_2813'),
  swiftPaymentCodeOption('id_swift_2814'),
  swiftPaymentCodeOption('id_swift_2815'),
  swiftPaymentCodeOption('id_swift_2821'),
  swiftPaymentCodeOption('id_swift_2822'),
  swiftPaymentCodeOption('id_swift_2823'),
  swiftPaymentCodeOption('id_swift_2824'),
  swiftPaymentCodeOption('id_swift_2825'),
  swiftPaymentCodeOption('id_swift_2826'),
  swiftPaymentCodeOption('id_swift_2827'),
  swiftPaymentCodeOption('id_swift_2828'),
  // PH — Filipinas
  swiftPaymentCodeOption('ph_swift_1010101000'),
  swiftPaymentCodeOption('ph_swift_1010102001'),
  swiftPaymentCodeOption('ph_swift_1010102002'),
  swiftPaymentCodeOption('ph_swift_1010103001'),
  swiftPaymentCodeOption('ph_swift_1010103002'),
  swiftPaymentCodeOption('ph_swift_1010201000'),
  swiftPaymentCodeOption('ph_swift_1010202001'),
  swiftPaymentCodeOption('ph_swift_1010202002'),
  swiftPaymentCodeOption('ph_swift_1010203001'),
  swiftPaymentCodeOption('ph_swift_1010203002'),
  swiftPaymentCodeOption('ph_swift_1010300000'),
  swiftPaymentCodeOption('ph_swift_1110100001'),
  swiftPaymentCodeOption('ph_swift_1110200001'),
  swiftPaymentCodeOption('ph_swift_1110200002'),
  swiftPaymentCodeOption('ph_swift_1110200003'),
  swiftPaymentCodeOption('ph_swift_1110200004'),
  swiftPaymentCodeOption('ph_swift_1110300001'),
  swiftPaymentCodeOption('ph_swift_1110300002'),
  swiftPaymentCodeOption('ph_swift_1110300003'),
  swiftPaymentCodeOption('ph_swift_1110300004'),
  swiftPaymentCodeOption('ph_swift_1110400001'),
  swiftPaymentCodeOption('ph_swift_1110400002'),
  swiftPaymentCodeOption('ph_swift_1110400003'),
  swiftPaymentCodeOption('ph_swift_1110400004'),
  swiftPaymentCodeOption('ph_swift_1110400005'),
  swiftPaymentCodeOption('ph_swift_1110400006'),
  swiftPaymentCodeOption('ph_swift_1110500001'),
  swiftPaymentCodeOption('ph_swift_1110500002'),
  swiftPaymentCodeOption('ph_swift_1110500003'),
  swiftPaymentCodeOption('ph_swift_1110500004'),
  swiftPaymentCodeOption('ph_swift_1110500005'),
  swiftPaymentCodeOption('ph_swift_1110600001'),
  swiftPaymentCodeOption('ph_swift_1110700001'),
  swiftPaymentCodeOption('ph_swift_1310000001'),
  swiftPaymentCodeOption('ph_swift_1310000002'),
  swiftPaymentCodeOption('ph_swift_1310000003'),
  swiftPaymentCodeOption('ph_swift_1410100001'),
  swiftPaymentCodeOption('ph_swift_1410100002'),
  swiftPaymentCodeOption('ph_swift_1410200001'),
  swiftPaymentCodeOption('ph_swift_1410200002'),
  swiftPaymentCodeOption('ph_swift_1410200003'),
  swiftPaymentCodeOption('ph_swift_1410200004'),
  swiftPaymentCodeOption('ph_swift_1410300000'),
  swiftPaymentCodeOption('ph_swift_1410400000'),
  swiftPaymentCodeOption('ph_swift_1410500000'),
  swiftPaymentCodeOption('ph_swift_1410600000'),
  swiftPaymentCodeOption('ph_swift_1510000001'),
  swiftPaymentCodeOption('ph_swift_1510000002'),
  swiftPaymentCodeOption('ph_swift_1510000003'),
  swiftPaymentCodeOption('ph_swift_1510000004'),
  swiftPaymentCodeOption('ph_swift_1510000005'),
  swiftPaymentCodeOption('ph_swift_1510000006'),
  swiftPaymentCodeOption('ph_swift_1510000007'),
  swiftPaymentCodeOption('ph_swift_1510000008'),
  swiftPaymentCodeOption('ph_swift_1510000009'),
  swiftPaymentCodeOption('ph_swift_1510000010'),
  swiftPaymentCodeOption('ph_swift_1510000011'),
  swiftPaymentCodeOption('ph_swift_1510000012'),
  swiftPaymentCodeOption('ph_swift_1510000013'),
  swiftPaymentCodeOption('ph_swift_1510000014'),
  swiftPaymentCodeOption('ph_swift_1510000015'),
  swiftPaymentCodeOption('ph_swift_1510000016'),
  swiftPaymentCodeOption('ph_swift_1510000017'),
  swiftPaymentCodeOption('ph_swift_1510000018'),
  swiftPaymentCodeOption('ph_swift_1510000019'),
  swiftPaymentCodeOption('ph_swift_1510000020'),
  swiftPaymentCodeOption('ph_swift_1510000021'),
  swiftPaymentCodeOption('ph_swift_1510000023'),
  swiftPaymentCodeOption('ph_swift_1510000024'),
  swiftPaymentCodeOption('ph_swift_1510000025'),
  swiftPaymentCodeOption('ph_swift_210000001'),
  swiftPaymentCodeOption('ph_swift_210000002'),
  swiftPaymentCodeOption('ph_swift_210000003'),
  swiftPaymentCodeOption('ph_swift_210000004'),
  swiftPaymentCodeOption('ph_swift_210000005'),
  swiftPaymentCodeOption('ph_swift_210000006'),
  swiftPaymentCodeOption('ph_swift_210000007'),
  swiftPaymentCodeOption('ph_swift_210000008'),
  swiftPaymentCodeOption('ph_swift_210000009'),
  swiftPaymentCodeOption('ph_swift_210000010'),
  swiftPaymentCodeOption('ph_swift_510100001'),
  swiftPaymentCodeOption('ph_swift_510200001'),
  swiftPaymentCodeOption('ph_swift_510200002'),
  swiftPaymentCodeOption('ph_swift_510301001'),
  swiftPaymentCodeOption('ph_swift_510301002'),
  swiftPaymentCodeOption('ph_swift_510301003'),
  swiftPaymentCodeOption('ph_swift_510302001'),
  swiftPaymentCodeOption('ph_swift_510302002'),
  swiftPaymentCodeOption('ph_swift_510302003'),
  swiftPaymentCodeOption('ph_swift_510303000'),
  swiftPaymentCodeOption('ph_swift_510401000'),
  swiftPaymentCodeOption('ph_swift_510402001'),
  swiftPaymentCodeOption('ph_swift_510402002'),
  swiftPaymentCodeOption('ph_swift_510402003'),
  swiftPaymentCodeOption('ph_swift_510402004'),
  swiftPaymentCodeOption('ph_swift_510402005'),
  swiftPaymentCodeOption('ph_swift_510402006'),
  swiftPaymentCodeOption('ph_swift_510402007'),
  swiftPaymentCodeOption('ph_swift_510500001'),
  swiftPaymentCodeOption('ph_swift_510500002'),
  swiftPaymentCodeOption('ph_swift_510500003'),
  swiftPaymentCodeOption('ph_swift_510500004'),
  swiftPaymentCodeOption('ph_swift_510500005'),
  swiftPaymentCodeOption('ph_swift_510600001'),
  swiftPaymentCodeOption('ph_swift_510600002'),
  swiftPaymentCodeOption('ph_swift_510600003'),
  swiftPaymentCodeOption('ph_swift_510600004'),
  swiftPaymentCodeOption('ph_swift_510600005'),
  swiftPaymentCodeOption('ph_swift_510700000'),
  swiftPaymentCodeOption('ph_swift_510800001'),
  swiftPaymentCodeOption('ph_swift_510800002'),
  swiftPaymentCodeOption('ph_swift_510900000'),
  swiftPaymentCodeOption('ph_swift_511000001'),
  swiftPaymentCodeOption('ph_swift_511000002'),
  swiftPaymentCodeOption('ph_swift_511100001'),
  swiftPaymentCodeOption('ph_swift_511100002'),
  swiftPaymentCodeOption('ph_swift_511100003'),
  swiftPaymentCodeOption('ph_swift_511200000'),
  swiftPaymentCodeOption('ph_swift_511300001'),
  swiftPaymentCodeOption('ph_swift_511300002'),
  swiftPaymentCodeOption('ph_swift_511300003'),
  swiftPaymentCodeOption('ph_swift_511300004'),
  swiftPaymentCodeOption('ph_swift_511300005'),
  swiftPaymentCodeOption('ph_swift_511300006'),
  swiftPaymentCodeOption('ph_swift_511300007'),
  swiftPaymentCodeOption('ph_swift_511300008'),
  swiftPaymentCodeOption('ph_swift_511400001'),
  swiftPaymentCodeOption('ph_swift_511400002'),
  swiftPaymentCodeOption('ph_swift_511400003'),
  swiftPaymentCodeOption('ph_swift_511400004'),
  swiftPaymentCodeOption('ph_swift_511400005'),
  swiftPaymentCodeOption('ph_swift_511500001'),
  swiftPaymentCodeOption('ph_swift_511500002'),
  swiftPaymentCodeOption('ph_swift_511500003'),
  swiftPaymentCodeOption('ph_swift_511500004'),
  swiftPaymentCodeOption('ph_swift_511600001'),
  swiftPaymentCodeOption('ph_swift_511600002'),
  swiftPaymentCodeOption('ph_swift_511600003'),
  swiftPaymentCodeOption('ph_swift_511700001'),
  swiftPaymentCodeOption('ph_swift_511700002'),
  swiftPaymentCodeOption('ph_swift_511700003'),
  swiftPaymentCodeOption('ph_swift_610000001'),
  swiftPaymentCodeOption('ph_swift_610000002'),
  swiftPaymentCodeOption('ph_swift_610000003'),
  swiftPaymentCodeOption('ph_swift_610000004'),
  swiftPaymentCodeOption('ph_swift_610000005'),
  swiftPaymentCodeOption('ph_swift_610000006'),
  swiftPaymentCodeOption('ph_swift_610000007'),
  swiftPaymentCodeOption('ph_swift_710100001'),
  swiftPaymentCodeOption('ph_swift_710100002'),
  swiftPaymentCodeOption('ph_swift_710100003'),
  swiftPaymentCodeOption('ph_swift_710100004'),
  swiftPaymentCodeOption('ph_swift_710100006'),
  swiftPaymentCodeOption('ph_swift_710100007'),
  swiftPaymentCodeOption('ph_swift_710100008'),
  swiftPaymentCodeOption('ph_swift_710100009'),
  swiftPaymentCodeOption('ph_swift_710200001'),
  swiftPaymentCodeOption('ph_swift_710200002'),
  swiftPaymentCodeOption('ph_swift_710200003'),
  swiftPaymentCodeOption('ph_swift_710200004'),
  swiftPaymentCodeOption('ph_swift_710200009'),
  swiftPaymentCodeOption('ph_swift_710300001'),
  swiftPaymentCodeOption('ph_swift_710300002'),
  swiftPaymentCodeOption('ph_swift_710300003'),
  swiftPaymentCodeOption('ph_swift_710300004'),
  swiftPaymentCodeOption('ph_swift_710300009'),
  swiftPaymentCodeOption('ph_swift_710400001'),
  swiftPaymentCodeOption('ph_swift_710400002'),
  swiftPaymentCodeOption('ph_swift_810101001'),
  swiftPaymentCodeOption('ph_swift_810101002'),
  swiftPaymentCodeOption('ph_swift_810101003'),
  swiftPaymentCodeOption('ph_swift_810101004'),
  swiftPaymentCodeOption('ph_swift_810101007'),
  swiftPaymentCodeOption('ph_swift_810101008'),
  swiftPaymentCodeOption('ph_swift_810101011'),
  swiftPaymentCodeOption('ph_swift_810102001'),
  swiftPaymentCodeOption('ph_swift_810102002'),
  swiftPaymentCodeOption('ph_swift_810102003'),
  swiftPaymentCodeOption('ph_swift_810102004'),
  swiftPaymentCodeOption('ph_swift_810102005'),
  swiftPaymentCodeOption('ph_swift_810102006'),
  swiftPaymentCodeOption('ph_swift_810102007'),
  swiftPaymentCodeOption('ph_swift_810102008'),
  swiftPaymentCodeOption('ph_swift_810102009'),
  swiftPaymentCodeOption('ph_swift_810201001'),
  swiftPaymentCodeOption('ph_swift_810201002'),
  swiftPaymentCodeOption('ph_swift_810201003'),
  swiftPaymentCodeOption('ph_swift_810201004'),
  swiftPaymentCodeOption('ph_swift_810202001'),
  swiftPaymentCodeOption('ph_swift_810202002'),
  swiftPaymentCodeOption('ph_swift_810202003'),
  swiftPaymentCodeOption('ph_swift_810202005'),
  swiftPaymentCodeOption('ph_swift_810202006'),
  swiftPaymentCodeOption('ph_swift_910100001'),
  swiftPaymentCodeOption('ph_swift_910100002'),
  swiftPaymentCodeOption('ph_swift_910100003'),
  swiftPaymentCodeOption('ph_swift_910100004'),
  swiftPaymentCodeOption('ph_swift_910100005'),
  swiftPaymentCodeOption('ph_swift_910100006'),
  swiftPaymentCodeOption('ph_swift_910100007'),
  swiftPaymentCodeOption('ph_swift_910100009'),
  swiftPaymentCodeOption('ph_swift_910100010'),
  swiftPaymentCodeOption('ph_swift_910100011'),
  swiftPaymentCodeOption('ph_swift_910200001'),
  swiftPaymentCodeOption('ph_swift_910200002'),
  swiftPaymentCodeOption('ph_swift_910200003'),
  swiftPaymentCodeOption('ph_swift_910200004'),
  swiftPaymentCodeOption('ph_swift_910200005'),
  swiftPaymentCodeOption('ph_swift_910200006'),
  swiftPaymentCodeOption('ph_swift_910200007'),
  swiftPaymentCodeOption('ph_swift_910200009'),
  swiftPaymentCodeOption('ph_swift_910200010'),
  swiftPaymentCodeOption('ph_swift_910200011'),
  // PK — Paquistão
  swiftPaymentCodeOption('pk_swift_9009'),
  swiftPaymentCodeOption('pk_swift_9010'),
  swiftPaymentCodeOption('pk_swift_9011'),
  swiftPaymentCodeOption('pk_swift_9012'),
  swiftPaymentCodeOption('pk_swift_9013'),
  swiftPaymentCodeOption('pk_swift_9014'),
  swiftPaymentCodeOption('pk_swift_9015'),
  swiftPaymentCodeOption('pk_swift_9016'),
  swiftPaymentCodeOption('pk_swift_9017'),
  swiftPaymentCodeOption('pk_swift_9018'),
  swiftPaymentCodeOption('pk_swift_9019'),
  swiftPaymentCodeOption('pk_swift_9020'),
  swiftPaymentCodeOption('pk_swift_9021'),
  swiftPaymentCodeOption('pk_swift_9022'),
  swiftPaymentCodeOption('pk_swift_9023'),
  swiftPaymentCodeOption('pk_swift_9024'),
  swiftPaymentCodeOption('pk_swift_9025'),
  swiftPaymentCodeOption('pk_swift_9026'),
  swiftPaymentCodeOption('pk_swift_9027'),
  swiftPaymentCodeOption('pk_swift_9028'),
  swiftPaymentCodeOption('pk_swift_9029'),
  swiftPaymentCodeOption('pk_swift_9030'),
  swiftPaymentCodeOption('pk_swift_9031'),
  swiftPaymentCodeOption('pk_swift_9032'),
  swiftPaymentCodeOption('pk_swift_9033'),
  swiftPaymentCodeOption('pk_swift_9041'),
  swiftPaymentCodeOption('pk_swift_9051'),
  swiftPaymentCodeOption('pk_swift_9052'),
  swiftPaymentCodeOption('pk_swift_9061'),
  swiftPaymentCodeOption('pk_swift_9071'),
  swiftPaymentCodeOption('pk_swift_9072'),
  swiftPaymentCodeOption('pk_swift_9073'),
  swiftPaymentCodeOption('pk_swift_9081'),
  swiftPaymentCodeOption('pk_swift_9082'),
  swiftPaymentCodeOption('pk_swift_9083'),
  swiftPaymentCodeOption('pk_swift_9084'),
  swiftPaymentCodeOption('pk_swift_9085'),
  swiftPaymentCodeOption('pk_swift_9086'),
  swiftPaymentCodeOption('pk_swift_9091'),
  swiftPaymentCodeOption('pk_swift_9092'),
  swiftPaymentCodeOption('pk_swift_9101'),
  swiftPaymentCodeOption('pk_swift_9102'),
  swiftPaymentCodeOption('pk_swift_9111'),
  swiftPaymentCodeOption('pk_swift_9121'),
  swiftPaymentCodeOption('pk_swift_9122'),
  swiftPaymentCodeOption('pk_swift_9124'),
  swiftPaymentCodeOption('pk_swift_9141'),
  swiftPaymentCodeOption('pk_swift_9146'),
  swiftPaymentCodeOption('pk_swift_9151'),
  swiftPaymentCodeOption('pk_swift_9152'),
  swiftPaymentCodeOption('pk_swift_9161'),
  swiftPaymentCodeOption('pk_swift_9171'),
  swiftPaymentCodeOption('pk_swift_9172'),
  swiftPaymentCodeOption('pk_swift_9173'),
  swiftPaymentCodeOption('pk_swift_9174'),
  swiftPaymentCodeOption('pk_swift_9181'),
  swiftPaymentCodeOption('pk_swift_9182'),
  swiftPaymentCodeOption('pk_swift_9183'),
  swiftPaymentCodeOption('pk_swift_9184'),
  swiftPaymentCodeOption('pk_swift_9185'),
  swiftPaymentCodeOption('pk_swift_9186'),
  swiftPaymentCodeOption('pk_swift_9191'),
  swiftPaymentCodeOption('pk_swift_9192'),
  swiftPaymentCodeOption('pk_swift_9193'),
  swiftPaymentCodeOption('pk_swift_9201'),
  swiftPaymentCodeOption('pk_swift_9202'),
  swiftPaymentCodeOption('pk_swift_9211'),
  swiftPaymentCodeOption('pk_swift_9212'),
  swiftPaymentCodeOption('pk_swift_9221'),
  swiftPaymentCodeOption('pk_swift_9222'),
  swiftPaymentCodeOption('pk_swift_9230'),
  swiftPaymentCodeOption('pk_swift_9231'),
  swiftPaymentCodeOption('pk_swift_9232'),
  swiftPaymentCodeOption('pk_swift_9233'),
  swiftPaymentCodeOption('pk_swift_9234'),
  swiftPaymentCodeOption('pk_swift_9235'),
  swiftPaymentCodeOption('pk_swift_9236'),
  swiftPaymentCodeOption('pk_swift_9237'),
  swiftPaymentCodeOption('pk_swift_9238'),
  swiftPaymentCodeOption('pk_swift_9239'),
  swiftPaymentCodeOption('pk_swift_9241'),
  swiftPaymentCodeOption('pk_swift_9242'),
  swiftPaymentCodeOption('pk_swift_9243'),
  swiftPaymentCodeOption('pk_swift_9244'),
  swiftPaymentCodeOption('pk_swift_9247'),
  swiftPaymentCodeOption('pk_swift_9248'),
  swiftPaymentCodeOption('pk_swift_9249'),
  swiftPaymentCodeOption('pk_swift_9250'),
  swiftPaymentCodeOption('pk_swift_9251'),
  swiftPaymentCodeOption('pk_swift_9261'),
  swiftPaymentCodeOption('pk_swift_9262'),
  swiftPaymentCodeOption('pk_swift_9271'),
  swiftPaymentCodeOption('pk_swift_9272'),
  swiftPaymentCodeOption('pk_swift_9273'),
  swiftPaymentCodeOption('pk_swift_9281'),
  swiftPaymentCodeOption('pk_swift_9291'),
  swiftPaymentCodeOption('pk_swift_9301'),
  swiftPaymentCodeOption('pk_swift_9302'),
  swiftPaymentCodeOption('pk_swift_9303'),
  swiftPaymentCodeOption('pk_swift_9311'),
  swiftPaymentCodeOption('pk_swift_9312'),
  swiftPaymentCodeOption('pk_swift_9322'),
  swiftPaymentCodeOption('pk_swift_9323'),
  swiftPaymentCodeOption('pk_swift_9324'),
  swiftPaymentCodeOption('pk_swift_9331'),
  swiftPaymentCodeOption('pk_swift_9332'),
  swiftPaymentCodeOption('pk_swift_9333'),
  swiftPaymentCodeOption('pk_swift_9341'),
  swiftPaymentCodeOption('pk_swift_9351'),
  swiftPaymentCodeOption('pk_swift_9361'),
  swiftPaymentCodeOption('pk_swift_9381'),
  swiftPaymentCodeOption('pk_swift_9391'),
  swiftPaymentCodeOption('pk_swift_9401'),
  swiftPaymentCodeOption('pk_swift_9412'),
  swiftPaymentCodeOption('pk_swift_9414'),
  swiftPaymentCodeOption('pk_swift_9421'),
  swiftPaymentCodeOption('pk_swift_9422'),
  swiftPaymentCodeOption('pk_swift_9423'),
  swiftPaymentCodeOption('pk_swift_9426'),
  swiftPaymentCodeOption('pk_swift_9431'),
  swiftPaymentCodeOption('pk_swift_9432'),
  swiftPaymentCodeOption('pk_swift_9433'),
  swiftPaymentCodeOption('pk_swift_9434'),
  swiftPaymentCodeOption('pk_swift_9435'),
  swiftPaymentCodeOption('pk_swift_9441'),
  swiftPaymentCodeOption('pk_swift_9442'),
  swiftPaymentCodeOption('pk_swift_9443'),
  swiftPaymentCodeOption('pk_swift_9444'),
  swiftPaymentCodeOption('pk_swift_9445'),
  swiftPaymentCodeOption('pk_swift_9448'),
  swiftPaymentCodeOption('pk_swift_9451'),
  swiftPaymentCodeOption('pk_swift_9452'),
  swiftPaymentCodeOption('pk_swift_9453'),
  swiftPaymentCodeOption('pk_swift_9454'),
  swiftPaymentCodeOption('pk_swift_9455'),
  swiftPaymentCodeOption('pk_swift_9456'),
  swiftPaymentCodeOption('pk_swift_9457'),
  swiftPaymentCodeOption('pk_swift_9458'),
  swiftPaymentCodeOption('pk_swift_9459'),
  swiftPaymentCodeOption('pk_swift_9461'),
  swiftPaymentCodeOption('pk_swift_9462'),
  swiftPaymentCodeOption('pk_swift_9463'),
  swiftPaymentCodeOption('pk_swift_9468'),
  swiftPaymentCodeOption('pk_swift_9469'),
  swiftPaymentCodeOption('pk_swift_9470'),
  swiftPaymentCodeOption('pk_swift_9471'),
  swiftPaymentCodeOption('pk_swift_9472'),
  swiftPaymentCodeOption('pk_swift_9473'),
  swiftPaymentCodeOption('pk_swift_9474'),
  swiftPaymentCodeOption('pk_swift_9475'),
  swiftPaymentCodeOption('pk_swift_9476'),
  swiftPaymentCodeOption('pk_swift_9477'),
  swiftPaymentCodeOption('pk_swift_9478'),
  swiftPaymentCodeOption('pk_swift_9479'),
  swiftPaymentCodeOption('pk_swift_9481'),
  swiftPaymentCodeOption('pk_swift_9482'),
  swiftPaymentCodeOption('pk_swift_9483'),
  swiftPaymentCodeOption('pk_swift_9484'),
  swiftPaymentCodeOption('pk_swift_9491'),
  swiftPaymentCodeOption('pk_swift_9492'),
  swiftPaymentCodeOption('pk_swift_9493'),
  swiftPaymentCodeOption('pk_swift_9494'),
  swiftPaymentCodeOption('pk_swift_9501'),
  swiftPaymentCodeOption('pk_swift_9502'),
  swiftPaymentCodeOption('pk_swift_9503'),
  swiftPaymentCodeOption('pk_swift_9521'),
  swiftPaymentCodeOption('pk_swift_9522'),
  swiftPaymentCodeOption('pk_swift_9523'),
  swiftPaymentCodeOption('pk_swift_9524'),
  swiftPaymentCodeOption('pk_swift_9525'),
  swiftPaymentCodeOption('pk_swift_9526'),
  swiftPaymentCodeOption('pk_swift_9527'),
  swiftPaymentCodeOption('pk_swift_9528'),
  swiftPaymentCodeOption('pk_swift_9529'),
  swiftPaymentCodeOption('pk_swift_9530'),
  swiftPaymentCodeOption('pk_swift_9531'),
  swiftPaymentCodeOption('pk_swift_9532'),
  swiftPaymentCodeOption('pk_swift_9533'),
  swiftPaymentCodeOption('pk_swift_9534'),
  swiftPaymentCodeOption('pk_swift_9535'),
  swiftPaymentCodeOption('pk_swift_9536'),
  swiftPaymentCodeOption('pk_swift_9537'),
  swiftPaymentCodeOption('pk_swift_9538'),
  swiftPaymentCodeOption('pk_swift_9539'),
  swiftPaymentCodeOption('pk_swift_9541'),
  swiftPaymentCodeOption('pk_swift_9542'),
  swiftPaymentCodeOption('pk_swift_9543'),
  swiftPaymentCodeOption('pk_swift_9544'),
  swiftPaymentCodeOption('pk_swift_9545'),
  swiftPaymentCodeOption('pk_swift_9546'),
  swiftPaymentCodeOption('pk_swift_9547'),
  swiftPaymentCodeOption('pk_swift_9548'),
  swiftPaymentCodeOption('pk_swift_9549'),
  swiftPaymentCodeOption('pk_swift_9551'),
  swiftPaymentCodeOption('pk_swift_9552'),
  swiftPaymentCodeOption('pk_swift_9553'),
  swiftPaymentCodeOption('pk_swift_9554'),
  swiftPaymentCodeOption('pk_swift_9555'),
  swiftPaymentCodeOption('pk_swift_9556'),
  swiftPaymentCodeOption('pk_swift_9561'),
  swiftPaymentCodeOption('pk_swift_9562'),
  swiftPaymentCodeOption('pk_swift_9563'),
  swiftPaymentCodeOption('pk_swift_9564'),
  swiftPaymentCodeOption('pk_swift_9565'),
  swiftPaymentCodeOption('pk_swift_9566'),
  swiftPaymentCodeOption('pk_swift_9572'),
  swiftPaymentCodeOption('pk_swift_9573'),
  swiftPaymentCodeOption('pk_swift_9574'),
  swiftPaymentCodeOption('pk_swift_9582'),
  swiftPaymentCodeOption('pk_swift_9583'),
  swiftPaymentCodeOption('pk_swift_9584'),
  swiftPaymentCodeOption('pk_swift_9592'),
  swiftPaymentCodeOption('pk_swift_9593'),
  swiftPaymentCodeOption('pk_swift_9594'),
  swiftPaymentCodeOption('pk_swift_9595'),
  swiftPaymentCodeOption('pk_swift_9596'),
  swiftPaymentCodeOption('pk_swift_9597'),
  swiftPaymentCodeOption('pk_swift_9602'),
  swiftPaymentCodeOption('pk_swift_9605'),
  swiftPaymentCodeOption('pk_swift_9606'),
  swiftPaymentCodeOption('pk_swift_9607'),
  swiftPaymentCodeOption('pk_swift_9608'),
  swiftPaymentCodeOption('pk_swift_9609'),
  swiftPaymentCodeOption('pk_swift_9612'),
  swiftPaymentCodeOption('pk_swift_9613'),
  swiftPaymentCodeOption('pk_swift_9614'),
  swiftPaymentCodeOption('pk_swift_9622'),
  swiftPaymentCodeOption('pk_swift_9623'),
  swiftPaymentCodeOption('pk_swift_9624'),
  swiftPaymentCodeOption('pk_swift_9625'),
  swiftPaymentCodeOption('pk_swift_9637'),
  swiftPaymentCodeOption('pk_swift_9638'),
  swiftPaymentCodeOption('pk_swift_9641'),
  swiftPaymentCodeOption('pk_swift_9642'),
  swiftPaymentCodeOption('pk_swift_9643'),
  swiftPaymentCodeOption('pk_swift_9644'),
  swiftPaymentCodeOption('pk_swift_9645'),
  swiftPaymentCodeOption('pk_swift_9646'),
  swiftPaymentCodeOption('pk_swift_9647'),
  swiftPaymentCodeOption('pk_swift_9648'),
  swiftPaymentCodeOption('pk_swift_9649'),
  swiftPaymentCodeOption('pk_swift_9650'),
  swiftPaymentCodeOption('pk_swift_9651'),
  swiftPaymentCodeOption('pk_swift_9652'),
  swiftPaymentCodeOption('pk_swift_9653'),
  swiftPaymentCodeOption('pk_swift_9654'),
  swiftPaymentCodeOption('pk_swift_9668'),
  swiftPaymentCodeOption('pk_swift_9669'),
  swiftPaymentCodeOption('pk_swift_9673'),
  swiftPaymentCodeOption('pk_swift_9674'),
  swiftPaymentCodeOption('pk_swift_9675'),
  swiftPaymentCodeOption('pk_swift_9676'),
  swiftPaymentCodeOption('pk_swift_9677'),
  swiftPaymentCodeOption('pk_swift_9678'),
  swiftPaymentCodeOption('pk_swift_9681'),
  swiftPaymentCodeOption('pk_swift_9682'),
  swiftPaymentCodeOption('pk_swift_9693'),
  swiftPaymentCodeOption('pk_swift_9694'),
  swiftPaymentCodeOption('pk_swift_9695'),
  swiftPaymentCodeOption('pk_swift_9697'),
  swiftPaymentCodeOption('pk_swift_9698'),
  swiftPaymentCodeOption('pk_swift_9699'),
  swiftPaymentCodeOption('pk_swift_9703'),
  swiftPaymentCodeOption('pk_swift_9707'),
  swiftPaymentCodeOption('pk_swift_9708'),
];
